/**
 * Utility functions for project related operations
 */

import { PlatformType } from '../types/project';
import { getPlatformDisplayName } from '../config/platformConfig';

/**
 * Derives a display name from an email address.
 * Example: john.doe@example.com -> "John Doe"
 * @param email - The email address
 * @returns Formatted name
 */
export const getNameFromEmail = (email: string): string => {
    if (!email) return '';
    const namePart = email.split('@')[0];
    return namePart
        .split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

/**
 * Generates 2-letter uppercase initials from a name.
 * Handles both "First Last" names and single words.
 * @param name - The name to generate initials from
 * @returns 2-letter uppercase initials
 */
export const getInitials = (name: string): string => {
    if (!name) return 'PR'; // Default fallback

    const words = name.trim().split(/\s+/);

    if (words.length >= 2) {
        // Extract first letters of first two words
        return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1 && words[0].length >= 2) {
        // If single word, use first two characters
        return words[0].substring(0, 2).toUpperCase();
    } else if (words.length === 1 && words[0].length === 1) {
        // If single character, duplicate it
        return (words[0] + words[0]).toUpperCase();
    }

    return 'PR';
};

/**
 * Formats a timestamp into a readable date string.
 * @param timestamp - Unix timestamp in seconds (optional)
 * @returns Formatted date string (e.g., "Dec 10, 2025")
 */
export const formatProjectDate = (timestamp: number | undefined): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

/**
 * Maps backend role strings to UI display roles.
 * @param backendRole - The role string from backend
 * @returns UI display role (e.g., "Editor", "Viewer")
 */
export const mapMemberRole = (backendRole: string): 'Owner' | 'Editor' | 'Viewer' => {
    if (backendRole === 'project_collaborator') return 'Editor';
    if (backendRole === 'project_viewer') return 'Viewer';
    return 'Viewer'; // Default
};

/**
 * Gets the display name for a platform type.
 * @param platformType - The platform type enum value
 * @returns Platform display name (e.g., "ServiceNow", "PowerApps", "TAP")
 */
export const getPlatformName = (platformType?: PlatformType): string => {
    return getPlatformDisplayName(platformType);
};
